import React, { useState } from "react"
import toast from "react-hot-toast"
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Clock,
  Loader2,
  X,
} from "lucide-react"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import { TiptapEditor } from "../../../../component/tiptapEditor"
import useSitSession from "../hooks/useSitSession"
import { paperDisplayTitle, type PaperSession } from "../types"
import { formatPaperTimer } from "../utils/formatPaperTime"
import { progressOf } from "../utils/paperStatus"
import PaperQuestionStem from "./PaperQuestionStem"
import "./SitMode.style.scss"

interface SitModeProps {
  session: PaperSession
  onSync: (session: PaperSession) => void
  onClose: () => void
  /** After successful submit — open analytics with marked session */
  onSubmitted?: (session: PaperSession) => void
}

const SitMode = ({ session: initial, onSync, onClose, onSubmitted }: SitModeProps) => {
  const {
    session,
    timeTotal,
    currentQ,
    isBusy,
    isSubmitting,
    saveError,
    updateAnswer,
    goToQuestion,
    goNext,
    saveAndExit,
    abandon,
    submit,
  } = useSitSession({ initialSession: initial, onSync })

  const [showLeaveMenu, setShowLeaveMenu] = useState(false)

  const runLeave = async (action: "exit" | "abandon") => {
    setShowLeaveMenu(false)
    try {
      if (action === "exit") await saveAndExit()
      else await abandon()
      onClose()
    } catch (err) {
      toast.error(
        getLearnerApiErrorMessage(
          err,
          action === "exit" ? "Could not save & exit" : "Could not abandon session"
        )
      )
    }
  }

  const submitPaper = async () => {
    try {
      const marked = await submit()
      if (onSubmitted) onSubmitted(marked)
      else onClose()
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not submit paper"))
    }
  }

  const { answered, total } = progressOf(session)
  const q = session.questions[currentQ]
  const locked = isBusy || isSubmitting
  const displayTitle = paperDisplayTitle(session)
  const showSubject =
    Boolean(session.subjectTitle?.trim()) &&
    displayTitle !== session.subjectTitle.trim()
  const progressPct = total > 0 ? (answered / total) * 100 : 0

  return (
    <div className="sit-mode">
      <header className="sit-mode__header">
        <div className="sit-mode__left">
          <div className="sit-mode__leave">
            <button
              type="button"
              onClick={() => setShowLeaveMenu((v) => !v)}
              aria-label="Leave paper"
              disabled={locked}
            >
              <X size={20} />
            </button>
            {showLeaveMenu && (
              <>
                <div
                  className="sit-mode__leave-backdrop"
                  onClick={() => setShowLeaveMenu(false)}
                  aria-hidden
                />
                <div className="sit-mode__leave-menu">
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => {
                      void runLeave("exit")
                    }}
                  >
                    <ChevronDown size={16} /> Save &amp; Exit
                  </button>
                  <button
                    type="button"
                    className="is-danger"
                    disabled={locked}
                    onClick={() => {
                      void runLeave("abandon")
                    }}
                  >
                    <AlertTriangle size={16} /> Abandon Session
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="sit-mode__title">
            <h2>{displayTitle}</h2>
            <p>
              {showSubject ? `${session.subjectTitle} · ` : ""}
              {answered}/{total} answered
              {saveError ? " · Save pending…" : ""}
            </p>
          </div>
        </div>

        <div className="sit-mode__right">
          <div className="sit-mode__timer">
            <Clock size={16} />
            {formatPaperTimer(timeTotal)}
          </div>
          <button
            type="button"
            className="sit-mode__submit"
            onClick={() => {
              void submitPaper()
            }}
            disabled={locked}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="is-spinning" /> Submitting…
              </>
            ) : (
              "Submit Paper"
            )}
          </button>
        </div>
      </header>

      <div
        className="sit-mode__progress"
        role="progressbar"
        aria-label="Questions answered"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={answered}
      >
        <div style={{ width: `${progressPct}%` }} />
      </div>

      <div className="sit-mode__body">
        <aside className="sit-mode__rail">
          <div className="sit-mode__rail-head">Questions</div>
          <div className="sit-mode__rail-list" role="list">
            {session.questions.map((item, i) => (
              <div
                key={`${item.questionId}-${i}`}
                role="listitem"
                className={`sit-mode__rail-item${
                  i === currentQ ? " is-current" : ""
                }`}
                aria-current={i === currentQ ? "step" : undefined}
              >
                <span
                  className={`sit-mode__pill${
                    i === currentQ
                      ? " is-current"
                      : item.status !== "unanswered"
                        ? " is-done"
                        : ""
                  }`}
                >
                  {i + 1}
                </span>
                <span className="sit-mode__rail-topic">{item.topic}</span>
              </div>
            ))}
          </div>
        </aside>

        <main className="sit-mode__main">
          <div className="sit-mode__mobile-nav" role="list">
            {session.questions.map((item, i) => (
              <span
                key={`m-${item.questionId}-${i}`}
                role="listitem"
                aria-current={i === currentQ ? "step" : undefined}
                className={`sit-mode__mobile-nav-item${
                  i === currentQ
                    ? " is-current"
                    : item.status !== "unanswered"
                      ? " is-done"
                      : ""
                }`}
              >
                {i + 1}
              </span>
            ))}
          </div>

          {q && (
            <div className="sit-mode__card">
              <div className="sit-mode__card-head">
                <div className="sit-mode__card-meta">
                  <span className="sit-mode__qbadge">
                    Question {currentQ + 1} of {total}
                  </span>
                  <span className="sit-mode__topic">{q.topic}</span>
                </div>
                <div className="sit-mode__card-meta">
                  {q.status !== "unanswered" && (
                    <span className="sit-mode__answered">
                      <CheckCircle2 size={14} /> Answered
                    </span>
                  )}
                  <span className="sit-mode__marks">[{q.maxScore} marks]</span>
                </div>
              </div>

              <div className="sit-mode__card-body">
                <div className="sit-mode__prompt">
                  <BookOpen size={16} />
                  <PaperQuestionStem
                    html={q.questionText}
                    artifacts={q.questionArtifacts}
                  />
                </div>
                <div
                  className={`sit-mode__answer-editor${
                    locked ? " is-locked" : ""
                  }`}
                >
                  <TiptapEditor
                    key={q.questionId}
                    id={`sit-answer-${q.questionId}`}
                    value={q.answerText}
                    height={220}
                    editable={!locked}
                    placeholder="Write your answer here…"
                    handleOnChange={(html) => updateAnswer(currentQ, html)}
                  />
                </div>
              </div>

              <div className="sit-mode__card-nav">
                <button
                  type="button"
                  onClick={() => goToQuestion(Math.max(0, currentQ - 1))}
                  disabled={currentQ === 0 || locked}
                >
                  ← Previous
                </button>
                <span>
                  {currentQ + 1} / {total}
                </span>
                {currentQ < total - 1 ? (
                  <button
                    type="button"
                    className="is-primary"
                    onClick={() => goNext()}
                    disabled={locked}
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    type="button"
                    className="is-submit"
                    onClick={() => {
                      void submitPaper()
                    }}
                    disabled={locked}
                  >
                    {isSubmitting ? (
                      <Loader2 size={16} className="is-spinning" />
                    ) : (
                      <CheckCircle2 size={16} />
                    )}
                    Submit Paper
                  </button>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default SitMode
